import { SlideshowSharp } from "@mui/icons-material";
import { Box, Button, Chip, Collapse, Paper, Typography } from "@mui/material";
import { useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import placeholder from "../../icons/placeholder.svg";
import { PMREvidencesProps, TQAChatEvidence } from "./models";
import { formatResponseText } from "./ViewTransript/ViewTranscript";

const HeaderAndValue = ({ header, value }) => {
  return (
    <>
      <Typography variant="h6" sx={{ fontSize: "10px", color: "rgba(66, 84, 102, 0.6)", marginTop: "20px" }}>
        {header}
      </Typography>
      <Box fontSize={14}>{formatResponseText(value, "13px", "13px")}</Box>
    </>
  );
};

const ExpandableText = ({ evidence, expanded, toggleExpand }: TQAChatEvidence) => {
  const text = evidence.complete_answer ?? evidence.retrieved_text_chunk ?? "";

  return (
    <Box>
      <Collapse collapsedSize={40} in={expanded}>
        <Box fontSize={14}>{formatResponseText(text, "13px", "13px")}</Box>
        {evidence.evidence_type === "transcript_full" && (
          <>
            <HeaderAndValue header={"EVIDENCE"} value={evidence.exhaustive_supporting_evidence}></HeaderAndValue>
            <HeaderAndValue header={"CONFIDENCE DETAILS"} value={evidence.detailed_confidence}></HeaderAndValue>
            <HeaderAndValue header={"COMPREHENSIVE NOTES"} value={evidence.comprehensive_notes}></HeaderAndValue>
            <HeaderAndValue header={"RESPONDENT ID"} value={evidence.respondent_id}></HeaderAndValue>
          </>
        )}

        <Box sx={{ mt: "24px", display: "flex", flexDirection: "column", gap: "10px" }}>
          <img src={placeholder} style={{ flexShrink: 0, width: "100%", display: "none" }} />

          <Box
            sx={{
              display: evidence.evidence_type !== "transcript_full" ? "flex" : "none",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontSize: "10px", color: "rgba(66, 84, 102, 0.6)" }}>
                {evidence.evidence_type === "transcript" ? "STUDY TITLE" : "PRESENTATION"}
              </Typography>
              {evidence.evidence_type === "transcript" ? (
                <Typography variant="h6" sx={{ fontSize: "13px", color: "rgba(37, 40, 42, 1)" }}>
                  {evidence.study_title}
                </Typography>
              ) : (
                <SlideshowSharp />
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ fontSize: "10px", color: "rgba(66, 84, 102, 0.6)" }}>
                {evidence.evidence_type === "transcript" ? "AUDIO FILE ID" : "SLIDE NUMBER"}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: "13px", color: "rgba(37, 40, 42, 1)" }}>
                {evidence.evidence_type === "transcript" ? `${evidence.audio_file_id}` : `${evidence.slide_number}`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Collapse>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "40px",
          overflow: "hidden",
        }}
      >
        <Button
          size="small"
          onClick={(e) => {
            toggleExpand(e);
          }}
          sx={{
            fontSize: "10px",
            color: "primary.dark",
            marginTop: "8px",
            minWidth: 0,
            display: text.length > 40 ? "flex" : "none",
            alignItems: "center",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      </Box>
    </Box>
  );
};

export default function PMREvidences({ evidences }: PMREvidencesProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Handler to toggle expansion:
  const handleCardClick = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Box sx={{ py: "1rem" }}>
      <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 900: 2, 1200: 2, 2000: 3 }}>
        <Masonry gutter="15px">
          {evidences?.map((record, i) => {
            const isExpanded = expandedIndex === i;
            return (
              <Paper key={i} elevation={1} sx={{ borderRadius: "16px", overflow: "hidden" }}>
                <Box sx={{ p: "0px", cursor: "pointer" }} onClick={() => handleCardClick(i)}>
                  <Box
                    sx={{
                      display: "flex",
                      position: "relative",
                      alignItems: "center",
                    }}
                  >
                    {record.study_title && (
                      <Typography sx={{ fontSize: "10px", color: "#545859", pl: "20px", pt: "0.2rem" }}>
                        {record.study_title}
                      </Typography>
                    )}
                    <Chip
                      label={`CONFIDENCE:  ${record.confidence_score}`}
                      sx={{
                        borderRadius: "0 0 0 8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        lineHeight: 1,
                        width: "100px",
                        height: "24px",
                        position: "absolute",
                        right: "0",
                        top: 0,
                        display: record.confidence_score ? "flex" : "none",
                        backgroundColor: "#8DC8E8",
                        color: "#fff",
                        fontSize: "10px",
                      }}
                    />
                  </Box>

                  <Box sx={{ px: "20px", pt: "20px" }}>
                    <Typography variant="h6" sx={{ fontSize: "10px", color: "rgba(66, 84, 102, 0.6)" }}>
                      QUESTION
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: "12px", color: "rgba(37, 40, 42, 1)" }}>
                      {record.question}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontSize: "10px", color: "rgba(66, 84, 102, 0.6)", marginTop: "10px" }}
                    >
                      {record.complete_answer ? "ANSWER" : "RELEVANT TEXT"}
                    </Typography>
                    <ExpandableText
                      evidence={record}
                      expanded={isExpanded}
                      toggleExpand={(e) => {
                        e.stopPropagation();
                        handleCardClick(i);
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Masonry>
      </ResponsiveMasonry>
    </Box>
  );
}
